import { useState } from "react"
import { IconXmarkOutline18 as XIcon } from "nucleo-ui-outline-18"

import type { EnumValue } from "@productlane/lib"
import { cn } from "@productlane/lib/src/cn"
import { Badge, Button } from "@productlane/ui"
import { inputVariants } from "@productlane/ui/src/components/form/input/input-variants"

import type { JsonKind } from "../model/tool-schema-fields"
import { parseListEntry } from "../model/typed-value"
import { FieldNotice } from "./field-notice"
import { HIGHLIGHT_RING_WITHIN } from "./highlight-metrics"

interface Props {
	name: string
	values: EnumValue[]
	/** Element types the entries are coerced to. Empty keeps them strings. */
	itemKinds?: JsonKind[]
	suggestions?: readonly EnumValue[]
	placeholder?: string
	emptyHint?: string
	onChange: (values: EnumValue[]) => void
}

export function ScalarTokenField({
	name,
	values,
	itemKinds,
	suggestions = [],
	placeholder = "Add a value",
	emptyHint,
	onChange,
}: Props) {
	const [entry, setEntry] = useState("")
	const [error, setError] = useState<string | null>(null)

	const add = () => {
		const parsed = parseListEntry(entry, itemKinds)
		// A silently dropped entry reads as a broken field, so say why instead.
		if (!parsed.ok) {
			setError(parsed.error)
			return
		}
		setError(null)
		if (values.some((current) => current === parsed.value)) {
			setEntry("")
			return
		}
		onChange([...values, parsed.value])
		setEntry("")
	}

	const toggle = (value: EnumValue) => {
		onChange(
			values.includes(value)
				? values.filter((current) => current !== value)
				: [...values, value],
		)
	}

	const unused = suggestions.filter((value) => !values.includes(value))

	return (
		<div className="space-y-1.5">
			<div className="flex items-start gap-2">
				<div
					className={cn(
						inputVariants(),
						HIGHLIGHT_RING_WITHIN,
						"h-auto min-h-8 flex-1 cursor-text flex-wrap items-center gap-1 px-2 py-1",
					)}
				>
					{values.map((value) => (
						<Badge
							key={String(value)}
							variant="green"
							className="cursor-default"
						>
							{String(value)}
							<button
								type="button"
								aria-label={`Remove ${String(value)}`}
								className="cursor-default"
								onClick={() => toggle(value)}
							>
								<XIcon className="h-3 w-3 flex-none" />
							</button>
						</Badge>
					))}
					<input
						name={name}
						value={entry}
						placeholder={values.length === 0 ? placeholder : undefined}
						className="h-6 min-w-24 flex-1 cursor-text bg-transparent text-sm text-primary placeholder:text-tertiary focus:outline-none"
						onChange={(event) => {
							setEntry(event.target.value)
							setError(null)
						}}
						onKeyDown={(event) => {
							if (event.key === "Enter") {
								event.preventDefault()
								add()
								return
							}
							// Backspace on an empty box takes back the last value, the way
							// every other token field behaves.
							if (event.key === "Backspace" && !entry && values.length > 0) {
								onChange(values.slice(0, -1))
							}
						}}
					/>
				</div>
				<Button variant="outline" onClick={add}>
					Add
				</Button>
			</div>

			{error ? (
				<FieldNotice tone="error" className="mt-0">
					{error}
				</FieldNotice>
			) : null}

			{unused.length > 0 ? (
				<div className="flex flex-wrap items-center gap-1">
					<span className="text-xs text-tertiary">From the schema:</span>
					{unused.map((value) => (
						<button
							key={String(value)}
							type="button"
							className="cursor-default"
							onClick={() => toggle(value)}
						>
							<Badge variant="dark-gray">{String(value)}</Badge>
						</button>
					))}
				</div>
			) : null}

			{values.length === 0 && emptyHint ? (
				<p className="text-xs text-tertiary">{emptyHint}</p>
			) : null}
		</div>
	)
}
